import {
  HasuraDataTableColumnDef,
  TablePageLayout,
  DataTableExRef,
} from '@kirz/mui-admin';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { DeleteRestore } from 'mdi-material-ui';
import React, { useCallback, useContext, useRef, useState } from 'react';

import { EstatesTable } from 'components/EstatesTable';
import { TagsGroup } from 'components/TagsGroup';
import {
  Finishing,
  getSaleQueueName,
  HouseTypes,
  RoomsNumbers,
  Statuses,
  Types,
} from 'constants/estate';
import { Permissions } from 'constants/permissions';
import { UserContext } from 'contexts';
import { SettingsContext } from 'contexts/SettingsContext';

export default function UserEstates() {
  const { user, hasPermission } = useContext(UserContext);
  const { tags, estateStatuses } = useContext(SettingsContext);
  const [selectedTab, setSelectedTab] = useState<string>('active');

  const tableRef = useRef<DataTableExRef>(null);
  const viewAchive = hasPermission(Permissions.ESTATES_ARCHIVE);
  const viewAllEstates = hasPermission(Permissions.ESTATES_VIEW_ALL);
  const viewAllEstatesSearch = hasPermission(
    Permissions.ESTATES_VIEW_ALL_SEARCH,
  );

  const filterColumns = useCallback(
    (columns: HasuraDataTableColumnDef[]) => {
      const visibleColumns = [
        { headerName: 'ID объекта', field: 'id', width: 60 },
        {
          headerName: 'Название ЖК',
          field: 'residentialComplex',
          type: 'relationship',
          minWidth: 140,
          flex: 1,
          selector: 'residentialComplex { *name* }',
        },
        {
          headerName: 'Кол-во комнат',
          width: 80,
          field: 'roomsNumber',
          type: 'select',
          items: RoomsNumbers,
        },
        {
          headerName: 'Дата сдачи',
          field: 'saleQueue',
          width: 90,
          type: 'relationship',
          selector: 'saleQueue { *releaseYear* releaseQuarter }',
          valueGetter({ value }) {
            return (
              value &&
              getSaleQueueName(value.releaseQuarter, value.releaseYear, true)
            );
          },
          onSort(sort) {
            return [{ saleQueue: { releaseDate: sort } }];
          },
        },
        {
          headerName: 'Отделка',
          field: 'finishing',
          width: 110,
          type: 'select',
          items: Finishing,
        },
        {
          headerName: 'Рекламная стоимость',
          field: 'price',
          width: 120,
          selector: 'price { adsPrice }',
          valueGetter({ value }) {
            return value?.adsPrice && `${value?.adsPrice.toLocaleString()}₽`;
          },
          onSort(sort) {
            return [{ price: { adsPrice: sort } }];
          },
        },
        {
          headerName: 'Статус',
          field: 'status',
          type: 'select',
          items: estateStatuses,
          width: 70,
        },
        {
          headerName: 'Тип объекта (ДДУ, Вторичка)',
          field: 'type',
          type: 'select',
          items: Types,
          width: 70,
        },
        {
          field: 'tags',
          headerName: 'Теги объекта',
          selector: 'tags { tagId }',
          sortable: false,
          width: 40,
          renderCell(params) {
            return (
              <TagsGroup ids={(params.value || []).map((x: any) => x.tagId)} />
            );
          },
        },
        {
          headerName: 'Собственник',
          field: 'owner',
          selector:
            'owner: clients(where:{isOwner: {_eq:true} }, limit: 1) { client { shortFullName } }',
          valueGetter({ value }) {
            return value?.length ? value[0].client.shortFullName : null;
          },
          width: 110,
          sortable: false,
        },
        {
          headerName: 'Реферер-клиент',
          field: 'referrer',
          width: 110,
          selector:
            'owner: clients(where:{isOwner: {_eq:true} }, limit: 1) { client { refererClient { shortFullName isRemoved } } }',
          type: 'relationship',
          isRemovedGetter: (row) =>
            row.owner?.[0]?.client?.refererClient?.isRemoved,
          valueGetter: ({ row }) =>
            row.owner?.[0]?.client?.refererClient?.shortFullName,
          sortable: false,
        },
        {
          headerName: 'Эксперт',
          field: 'mainExpert',
          width: 110,
          selector:
            'secondaryExpertId client { user { shortFullName isRemoved } } user { data { shortFullName isRemoved } }',
          type: 'relationship',
          isRemovedGetter: (row) =>
            (row.secondaryExpertId ? row.user?.data : row.client?.user)
              ?.isRemoved,
          valueGetter: ({ row }) =>
            row.secondaryExpertId
              ? row.user?.data?.shortFullName
              : row.client?.user?.shortFullName,
          sortable: false,
        },
      ] as HasuraDataTableColumnDef[];

      const visibleFields = visibleColumns.map((x) => x.field);
      const hiddenColumns = columns
        .filter(
          (x) =>
            !visibleFields.includes(x.field) &&
            !['client', 'referrer', 'user'].includes(x.field),
        )
        .map((x) => ({ ...x, hidden: true }));

      const ignore = [
        ...(!['assignments', 'secondary'].includes(selectedTab)
          ? ['sellingCommissionSum']
          : []),
      ];

      const result = [...visibleColumns, ...hiddenColumns].filter(
        (x) => !ignore.includes(x.field),
      );

      if (selectedTab === 'deleted' && viewAchive) {
        return [
          ...result,
          {
            field: 'recover',
            type: 'icon-button',
            icon: <DeleteRestore />,
            tooltip: 'Восстановить',
            selector: false,
            tabs: ['deleted'],
            onClick: async (
              row,
              {
                configuration: { hasura },
                notifications: { showConfirm, showAlert },
              },
            ) => {
              if (
                !(await showConfirm({
                  title: 'Восстановить объект?',
                  accept: 'Восстановить',
                  cancel: 'Отмена',
                }))
              ) {
                return;
              }

              await hasura.request({
                type: 'mutation',
                action: 'update',
                source: 'estate',
                where: { id: { _eq: row.id } },
                set: { isRemoved: false },
              });

              showAlert('Восстановлено', 'success');
              await tableRef.current!.reload();
            },
          } as HasuraDataTableColumnDef,
        ];
      }

      return result;
    },
    [selectedTab, viewAchive, estateStatuses],
  );

  return (
    <TablePageLayout
      title="Объекты"
      actionContent={
        <Button
          sx={{ ml: 'auto' }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            tableRef.current?.openFormDialog();
          }}
        >
          Добавить
        </Button>
      }
    >
      <EstatesTable
        id="estates-table"
        ref={tableRef}
        skeletonRowsCount={50}
        filterColumns={filterColumns}
        disableRemovedFilter
        deletable={selectedTab !== 'deleted'}
        tabsFilter={{
          tabs: [
            {
              id: 'active',
              label: 'Все',
              filter: { isRemoved: { _eq: false } },
            },
            {
              id: 'assignments',
              label: 'Переуступка',
              filter: {
                status: { _in: [Statuses.FOR_SALE, Statuses.DEPOSIT_MADE] },
                type: { _neq: 'secondary' },
                isRemoved: { _eq: false },
              },
            },
            {
              id: 'secondary',
              label: 'Вторичка',
              filter: {
                status: { _eq: Statuses.FOR_SALE },
                type: { _eq: 'secondary' },
                isRemoved: { _eq: false },
              },
            },
            ...(viewAchive
              ? [
                  {
                    id: 'deleted',
                    label: 'Удаленные',
                    filter: { isRemoved: { _eq: true } },
                  },
                ]
              : []),
          ],
          tabsProps: {
            onChange(e, value) {
              setSelectedTab(value);
            },
          },
        }}
        searchFilter={{
          inputProps: {
            placeholder: 'Поиск по ID, ЖК, адресу, ФИО собственника',
          },
          minLength: 0,
          filter: (search) => ({
            _and: [
              ...(!viewAllEstates &&
              (!viewAllEstatesSearch || search[0].length < 3)
                ? [
                    {
                      clients: {
                        client: {
                          participants: {
                            userId: { _eq: user.id },
                          },
                        },
                      },
                    },
                  ]
                : []),
              ...(search[0].length > 0
                ? [
                    {
                      _or: [
                        ...search.flatMap((str) => [
                          ...(Number.isInteger(parseInt(str, 10)) &&
                          str.length < 7
                            ? [{ id: { _eq: parseInt(str, 10) } }]
                            : []),
                          {
                            address: { _ilike: `%${str}%` },
                            residentialComplexId: { _isNull: true },
                          },
                        ]),
                        {
                          residentialComplex: {
                            _or: [
                              ...search.map((str) => ({
                                name: { _ilike: `%${str}%` },
                              })),
                              {
                                addresses: {
                                  _or: search.map((str) => ({
                                    address: { _ilike: `%${str}%` },
                                  })),
                                },
                              },
                            ],
                          },
                        },
                        {
                          clients: {
                            _or: [
                              {
                                isOwner: { _eq: true },
                                client: {
                                  _or: search.map((str) => ({
                                    fullName: { _ilike: `%${str}%` },
                                  })),
                                },
                              },
                            ],
                          },
                        },
                      ],
                    },
                  ]
                : []),
            ],
          }),
        }}
        customFilter={{
          filters: [
            {
              type: 'autocomplete',
              mode: 'hasura',
              name: 'Жилой комплекс',
              field: 'residentialComplexId',
              source: 'residentialComplex',
              selection: 'id name',
              itemText: 'name',
              multiple: true,
              filter: (value) =>
                value.length ? { residentialComplexId: { _in: value } } : {},
            },
            {
              type: 'autocomplete',
              name: 'Кол. комнат',
              field: 'roomsNumber',
              options: RoomsNumbers,
              multiple: true,
              filter: (value) =>
                value.length ? { roomsNumber: { _in: value } } : {},
            },
            {
              type: 'autocomplete',
              name: 'Тип дома',
              field: 'houseType',
              options: HouseTypes,
              multiple: true,
              filter: (value) =>
                value.length ? { houseType: { _in: value } } : {},
            },
            {
              type: 'autocomplete',
              name: 'Отделка',
              field: 'finishing',
              options: Finishing,
              multiple: true,
              filter: (value) =>
                value.length ? { finishing: { _in: value } } : {},
            },
            {
              type: 'autocomplete',
              name: 'Статус',
              field: 'status',
              options: estateStatuses,
              multiple: true,
              filter: (value) =>
                value.length ? { status: { _in: value } } : {},
            },
            {
              type: 'autocomplete',
              name: 'Тип договора',
              field: 'type',
              options: Types,
              multiple: true,
              filter: (value) => ({ type: { _in: value } }),
              width: 500,
            },
            {
              type: 'autocomplete',
              name: 'Застройщик',
              field: 'developer',
              mode: 'hasura',
              source: 'residentialComplex',
              preset: 'suggestion',
            },
            {
              type: 'autocomplete',
              name: 'Теги',
              options: tags
                .filter((x) => x.entity === 'estate')
                .map((x) => ({ value: x.id, text: x.name })),
              field: 'tagId',
              filter: (value) =>
                value.length ? { tags: { tagId: { _in: value } } } : {},
              multiple: true,
            },
            {
              type: 'autocomplete',
              mode: 'hasura',
              name: 'Собственник',
              field: 'owner',
              source: 'client',
              selection: 'id fullName',
              itemText: 'fullName',
              filter: (value) => ({
                clients: {
                  isOwner: { _eq: true },
                  clientId: { _eq: value },
                },
              }),
            },
            {
              type: 'autocomplete',
              mode: 'hasura',
              name: 'Реферер-клиент',
              field: 'referrer',
              source: 'client',
              selection: 'id fullName',
              itemText: 'fullName',
              filter: (value) => ({
                clients: {
                  isOwner: { _eq: true },
                  client: { refererClientId: { _eq: value } },
                },
              }),
            },
            {
              type: 'autocomplete',
              mode: 'hasura',
              name: 'Эксперт',
              field: 'user',
              source: 'user',
              selection: 'id fullName',
              itemText: 'fullName',
              multiple: true,
              filter: (value) =>
                value.length
                  ? {
                      user: { userId: { _in: value } },
                    }
                  : {},
            },
          ],
        }}
      />
    </TablePageLayout>
  );
}
